export interface TemplateVariant {
  id: string
  label: string
}

export interface TemplateDefinition {
  id: string
  name: string
  namespace: string
  keys: string[]
  variants?: TemplateVariant[]
  defaultVariant?: string
}

export const EMAIL_TEMPLATES: TemplateDefinition[] = [
  {
    id: 'feedbackRequest',
    name: 'Palautepyyntö',
    namespace: 'feedback',
    keys: [
      'feedback:notifications.email.feedbackRequest.subject',
      'feedback:notifications.email.feedbackRequest.heading',
      'feedback:notifications.email.feedbackRequest.greeting',
      'feedback:notifications.email.feedbackRequest.message1',
      'feedback:notifications.email.feedbackRequest.message2',
      'feedback:notifications.email.feedbackRequest.message3',
      'feedback:notifications.email.feedbackRequest.button',
      'feedback:notifications.email.feedbackRequest.signOff',
      'feedback:notifications.email.feedbackRequest.disclaimer',
      'feedback:notifications.email.feedbackRequest.optOutLink',
    ],
  },
  {
    id: 'feedbackReceived',
    name: 'Uusi ostajaprofiili',
    namespace: 'feedback',
    keys: [
      'feedback:notifications.email.feedbackReceived.subject',
      'feedback:notifications.email.feedbackReceived.heading',
      'feedback:notifications.email.feedbackReceived.greeting',
      'feedback:notifications.email.feedbackReceived.messagePart1',
      'feedback:notifications.email.feedbackReceived.messagePart2',
      'feedback:notifications.email.feedbackReceived.postscript',
      'feedback:notifications.email.feedbackReceived.button',
    ],
  },
  {
    id: 'realtorLinkFeedbackReceived',
    name: 'Uusi kontakti linkin kautta',
    namespace: 'feedback',
    keys: [
      'feedback:notifications.email.realtorLinkFeedbackReceived.subject',
      'feedback:notifications.email.realtorLinkFeedbackReceived.heading',
      'feedback:notifications.email.realtorLinkFeedbackReceived.greeting',
      'feedback:notifications.email.realtorLinkFeedbackReceived.messagePart1',
      'feedback:notifications.email.realtorLinkFeedbackReceived.messagePart2',
      'feedback:notifications.email.realtorLinkFeedbackReceived.postscript',
      'feedback:notifications.email.realtorLinkFeedbackReceived.button',
    ],
  },
  {
    id: 'contactRequestResponse',
    name: 'Yhteydenottopyyntövastaus',
    namespace: 'contacts',
    keys: [
      'contacts:notifications.email.contactRequestResponse.accepted.heading',
      'contacts:notifications.email.contactRequestResponse.accepted.message',
      'contacts:notifications.email.contactRequestResponse.accepted.button',
      'contacts:notifications.email.contactRequestResponse.rejected.heading',
      'contacts:notifications.email.contactRequestResponse.rejected.message',
      'contacts:notifications.email.contactRequestResponse.rejected.messageWithReason',
      'contacts:notifications.email.contactRequestResponse.rejected.button',
    ],
    variants: [
      { id: 'accepted', label: 'Hyväksytty' },
      { id: 'rejected', label: 'Hylätty' },
      { id: 'rejectedWithReason', label: 'Hylätty + syy' },
    ],
    defaultVariant: 'accepted',
  },
  {
    id: 'leadPurchased',
    name: 'Liidi ostettu',
    namespace: 'leads',
    keys: [
      'leads:notifications.email.leadPurchased.heading',
      'leads:notifications.email.leadPurchased.intro',
      'leads:notifications.email.leadPurchased.leadLabel',
      'leads:notifications.email.leadPurchased.propertyLabel',
      'leads:notifications.email.leadPurchased.detailsLabel',
      'leads:notifications.email.leadPurchased.sourceLabel',
      'leads:notifications.email.leadPurchased.button',
    ],
    variants: [
      { id: 'representation', label: 'Myyntikohde' },
      { id: 'prospect', label: 'Prospekti' },
    ],
    defaultVariant: 'representation',
  },
  {
    id: 'leadOutcome',
    name: 'Liidin lopputulos',
    namespace: 'leads',
    keys: [
      'leads:notifications.email.leadOutcome.heading',
      'leads:notifications.email.leadOutcome.intro',
      'leads:notifications.email.leadOutcome.leadLabel',
      'leads:notifications.email.leadOutcome.propertyLabel',
      'leads:notifications.email.leadOutcome.detailsLabel',
      'leads:notifications.email.leadOutcome.sourceLabel',
      'leads:notifications.email.leadOutcome.button',
    ],
    variants: [
      { id: 'representation', label: 'Myyntikohde' },
      { id: 'prospect', label: 'Prospekti' },
    ],
    defaultVariant: 'representation',
  },
  {
    id: 'inboundIngest',
    name: 'Saapuva sähköposti käsitelty',
    namespace: 'common',
    keys: [
      'common:notifications.email.inboundIngest.heading',
      'common:notifications.email.inboundIngest.successMessage',
      'common:notifications.email.inboundIngest.contactCreated',
      'common:notifications.email.inboundIngest.propertyCreated',
      'common:notifications.email.inboundIngest.button',
    ],
    variants: [
      { id: 'both', label: 'Kontakti + myyntikohde' },
      { id: 'property', label: 'Myyntikohde' },
      { id: 'prospect', label: 'Prospekti (ei hintaa)' },
      { id: 'contact', label: 'Kontakti' },
    ],
    defaultVariant: 'both',
  },
  {
    id: 'inboundError',
    name: 'Saapuvan sähköpostin virhe',
    namespace: 'common',
    keys: [
      'common:notifications.email.inboundError.heading',
      'common:notifications.email.inboundError.message',
      'common:notifications.email.inboundError.button',
    ],
  },
  {
    id: 'welcome',
    name: 'Tervetuloa',
    namespace: 'user',
    keys: [
      'user:notifications.email.welcome.subject',
      'user:notifications.email.welcome.heading',
      'user:notifications.email.welcome.messageWithInviter',
      'user:notifications.email.welcome.messageWithInviterAndOrganisation',
      'user:notifications.email.welcome.messageWithoutInviter',
      'user:notifications.email.welcome.valueProposition',
      'user:notifications.email.welcome.loginDetails',
      'user:notifications.email.welcome.button',
      'user:notifications.email.welcome.signOff',
    ],
    variants: [
      { id: 'withInviterAndOrganisation', label: 'Kutsuja + organisaatio' },
      { id: 'withInviter', label: 'Vain kutsujalla' },
      { id: 'withoutInviter', label: 'Ilman kutsujaa' },
    ],
    defaultVariant: 'withInviterAndOrganisation',
  },
  {
    id: 'realtorActivation',
    name: 'Välittäjän aktivointisähköposti',
    namespace: 'user',
    keys: [
      'user:notifications.email.realtorActivation.subject',
      'user:notifications.email.realtorActivation.greeting',
      'user:notifications.email.realtorActivation.heading',
      'user:notifications.email.realtorActivation.intro',
      'user:notifications.email.realtorActivation.introParagraph2',
      'user:notifications.email.realtorActivation.button',
      'user:notifications.email.realtorActivation.signOff',
    ],
  },
]

export const SMS_TEMPLATES: TemplateDefinition[] = [
  {
    id: 'feedbackRequest',
    name: 'Palautepyyntö',
    namespace: 'feedback',
    keys: ['feedback:notifications.sms.feedbackRequest'],
  },
  {
    id: 'contactRequest',
    name: 'Yhteydenottopyyntö',
    namespace: 'contacts',
    keys: [
      'contacts:notifications.sms.contactRequest.active',
      'contacts:notifications.sms.contactRequest.preliminary',
    ],
    variants: [
      { id: 'active', label: 'Aktiivinen' },
      { id: 'preliminary', label: 'Ennakkomarkkinointi' },
    ],
    defaultVariant: 'active',
  },
]
